import React, { useEffect, useState, useRef } from "react";
import {
  Avatar,
  Typography,
  Box,
  CircularProgress,
  Grid,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Alert,
  Tooltip,
  Tabs,
  Tab,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import ChatIcon from "@mui/icons-material/Chat";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate } from "react-router-dom";
import { isLoggedIn } from "../session";
import { db, auth, storage, updateUser, updateUserProfile } from "../firebase";
import {
  doc,
  onSnapshot,
  collection,
  query,
  orderBy,
  where,
  updateDoc,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import Header1 from "./Header1";
import BottomNav from "./BottomNav";

const Miperfil = () => {
  const navigate = useNavigate();
  const { uid } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [isOwner, setIsOwner] = useState(false);

  // Edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [editName, setEditName] = useState("");
  const [editPhone, setEditPhone] = useState("");
  const [editPoint, setEditPoint] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [saving, setSaving] = useState(false);

  // Avatar upload
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const photoInputRef = useRef(null);

  // Presentation photos
  const [uploadingPresentation, setUploadingPresentation] = useState(false);
  const presentationInputRef = useRef(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [tab, setTab] = useState(0);

  // Snackbar
  const [snack, setSnack] = useState({ open: false, msg: "", severity: "success" });

  useEffect(() => {
    if (!isLoggedIn()) { navigate("/login"); return; }
    const currentUser = auth.currentUser;
    if (currentUser) setIsOwner(currentUser.uid === uid);

    const unsub = onSnapshot(doc(db, "users", uid), (snap) => {
      if (snap.exists()) setProfile(snap.data());
      setLoading(false);
    });
    return unsub;
  }, [navigate, uid]);

  useEffect(() => {
    const q = query(collection(db, "products"), where("uid", "==", uid), orderBy("createdAt", "desc"));
    return onSnapshot(q, (snap) => setProducts(snap.docs.map((d) => ({ id: d.id, ...d.data() }))));
  }, [uid]);

  const openEdit = () => {
    setEditName(profile?.displayName || "");
    setEditPhone(profile?.phone || "");
    setEditPoint(profile?.point || "");
    setEditStatus(profile?.status || "");
    setEditOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateUser(uid, editName, editPhone, editPoint);
      await updateUserProfile(uid, { status: editStatus });
      setSnack({ open: true, msg: "Perfil actualizado", severity: "success" });
      setEditOpen(false);
    } catch {
      setSnack({ open: true, msg: "Error al guardar", severity: "error" });
    }
    setSaving(false);
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const storageRef = ref(storage, `profileImages/${uid}_${Date.now()}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      await updateUserProfile(uid, { profileImageUrl: url });
      setSnack({ open: true, msg: "Foto de perfil actualizada", severity: "success" });
    } catch {
      setSnack({ open: true, msg: "Error al subir la foto", severity: "error" });
    }
    setUploadingPhoto(false);
  };

  const handlePresentationUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingPresentation(true);
    try {
      const urls = await Promise.all(
        files.map(async (file) => {
          const storageRef = ref(storage, `presentationPhotos/${uid}/${Date.now()}_${file.name}`);
          await uploadBytes(storageRef, file);
          return getDownloadURL(storageRef);
        })
      );
      await updateDoc(doc(db, "users", uid), { presentationPhotos: arrayUnion(...urls) });
      setSnack({ open: true, msg: `${urls.length} foto(s) agregada(s)`, severity: "success" });
    } catch {
      setSnack({ open: true, msg: "Error al subir fotos", severity: "error" });
    }
    setUploadingPresentation(false);
    e.target.value = "";
  };

  const handleDeletePresentationPhoto = async (url) => {
    try {
      await updateDoc(doc(db, "users", uid), { presentationPhotos: arrayRemove(url) });
      try { await deleteObject(ref(storage, url)); } catch (_) {}
      setSnack({ open: true, msg: "Foto eliminada", severity: "success" });
    } catch {
      setSnack({ open: true, msg: "Error al eliminar", severity: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  const presentationPhotos = profile?.presentationPhotos || [];

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f6fa", pb: 12 }}>
      <Header1 />

      {/* Hero */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #1976d2 0%, #7c3aed 100%)",
          pt: 10,
          pb: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Box sx={{ position: "relative", mb: 1.5 }}>
          <Avatar
            src={profile?.profileImageUrl}
            sx={{
              width: 100, height: 100, fontSize: 36, fontWeight: 700,
              border: "3px solid rgba(255,255,255,0.7)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.2)",
              bgcolor: "rgba(255,255,255,0.25)", color: "#fff",
            }}
          >
            {!profile?.profileImageUrl && (profile?.displayName?.[0] || "?")}
          </Avatar>
          {isOwner && (
            <>
              <input type="file" accept="image/*" ref={photoInputRef} style={{ display: "none" }} onChange={handleAvatarChange} />
              <IconButton
                onClick={() => photoInputRef.current.click()}
                disabled={uploadingPhoto}
                size="small"
                sx={{ position: "absolute", bottom: 0, right: 0, bgcolor: "#fff", boxShadow: "0 2px 8px rgba(0,0,0,0.15)", p: 0.6, "&:hover": { bgcolor: "#f0f4ff" } }}
              >
                {uploadingPhoto ? <CircularProgress size={16} /> : <CameraAltIcon sx={{ fontSize: 16, color: "#1976d2" }} />}
              </IconButton>
            </>
          )}
        </Box>

        <Typography variant="h6" sx={{ color: "#fff", fontWeight: 700 }}>
          {profile?.displayName || "Usuario"}
        </Typography>
        <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.8)" }}>
          {profile?.email}
        </Typography>

        {/* Estado / bio */}
        {profile?.status && (
          <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.95)", mt: 0.8, fontStyle: "italic", px: 3, textAlign: "center" }}>
            "{profile.status}"
          </Typography>
        )}

        <Box sx={{ display: "flex", gap: 1, mt: 1.2, flexWrap: "wrap", justifyContent: "center" }}>
          {profile?.phone && (
            <Chip icon={<PhoneIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.9) !important" }} />} label={profile.phone} size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 12 }} />
          )}
          {profile?.point && (
            <Chip icon={<LocationOnIcon sx={{ fontSize: 14, color: "rgba(255,255,255,0.9) !important" }} />} label={profile.point} size="small"
              sx={{ bgcolor: "rgba(255,255,255,0.15)", color: "#fff", fontSize: 12 }} />
          )}
        </Box>

        <Box sx={{ mt: 2, mb: 1 }}>
          {isOwner ? (
            <Button variant="outlined" startIcon={<EditIcon />} onClick={openEdit}
              sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.6)", borderRadius: 3, fontWeight: 600, "&:hover": { bgcolor: "rgba(255,255,255,0.1)", borderColor: "#fff" } }}>
              Editar perfil
            </Button>
          ) : (
            <Button variant="contained" startIcon={<ChatIcon />}
              sx={{ bgcolor: "#fff", color: "#1976d2", borderRadius: 3, fontWeight: 700, "&:hover": { bgcolor: "#e8f0fe" } }}>
              Chatear
            </Button>
          )}
        </Box>
      </Box>

      {/* Tabs */}
      <Box sx={{ bgcolor: "#fff", borderBottom: "1px solid #e2e8f0", mt: 2 }}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="fullWidth"
          TabIndicatorProps={{ style: { background: "#1976d2", height: 3 } }}
        >
          <Tab label="Fotos" sx={{ fontWeight: 600, textTransform: "none", fontSize: 14 }} />
          <Tab label={`Publicaciones${products.length ? ` · ${products.length}` : ""}`} sx={{ fontWeight: 600, textTransform: "none", fontSize: 14 }} />
        </Tabs>
      </Box>

      {/* Fotos de presentación */}
      {tab === 0 && <Box sx={{ px: 2, mt: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1e293b" }}>
            {presentationPhotos.length} foto{presentationPhotos.length !== 1 ? "s" : ""}
          </Typography>
          {isOwner && (
            <>
              <input
                type="file"
                accept="image/*"
                multiple
                ref={presentationInputRef}
                style={{ display: "none" }}
                onChange={handlePresentationUpload}
              />
              <Tooltip title="Agregar fotos">
                <IconButton
                  onClick={() => presentationInputRef.current.click()}
                  disabled={uploadingPresentation}
                  sx={{ bgcolor: "#e8f0fe", color: "#1976d2", "&:hover": { bgcolor: "#d0e4ff" } }}
                  size="small"
                >
                  {uploadingPresentation ? <CircularProgress size={18} /> : <AddPhotoAlternateIcon />}
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>

        {presentationPhotos.length === 0 ? (
          <Box
            sx={{ borderRadius: 3, border: "2px dashed #cbd5e1", py: 4, textAlign: "center", color: "#94a3b8", cursor: isOwner ? "pointer" : "default" }}
            onClick={() => isOwner && presentationInputRef.current.click()}
          >
            <AddPhotoAlternateIcon sx={{ fontSize: 36, mb: 0.5, opacity: 0.5 }} />
            <Typography variant="body2">{isOwner ? "Agregá fotos de presentación" : "Sin fotos aún"}</Typography>
          </Box>
        ) : (
          <Grid container spacing={1}>
            {presentationPhotos.map((url, idx) => (
              <Grid item key={idx} xs={4} sm={3}>
                <Box
                  sx={{ position: "relative", borderRadius: 2, overflow: "hidden", aspectRatio: "1", cursor: "pointer", "&:hover .delete-btn": { opacity: 1 } }}
                  onClick={() => setLightboxImg(url)}
                >
                  <Box component="img" src={url} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                  {isOwner && (
                    <IconButton
                      className="delete-btn"
                      size="small"
                      onClick={(e) => { e.stopPropagation(); handleDeletePresentationPhoto(url); }}
                      sx={{
                        position: "absolute", top: 4, right: 4,
                        bgcolor: "rgba(0,0,0,0.55)", color: "#fff", opacity: 0,
                        transition: "opacity 0.2s", p: 0.4,
                        "&:hover": { bgcolor: "rgba(220,38,38,0.85)" },
                      }}
                    >
                      <DeleteIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  )}
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>}

      {/* Publicaciones */}
      {tab === 1 && <Box sx={{ px: 2, mt: 2.5 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: "#1e293b" }}>
          Publicaciones · {products.length}
        </Typography>

        {products.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 6, color: "#94a3b8" }}>
            <Typography>Sin publicaciones aún</Typography>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {products.map((product) => (
              <Grid item key={product.id} xs={6} sm={4} md={3}>
                <Box sx={{ bgcolor: "#fff", borderRadius: 3, overflow: "hidden", boxShadow: "0 2px 10px rgba(0,0,0,0.08)", cursor: "pointer", "&:hover": { boxShadow: "0 4px 20px rgba(0,0,0,0.13)", transform: "translateY(-2px)" }, transition: "all 0.2s" }}>
                  <Box component="img" src={product.imageUrl} alt={product.title} sx={{ width: "100%", aspectRatio: "1", objectFit: "cover" }} />
                  <Box sx={{ p: 1.2 }}>
                    <Typography sx={{ fontWeight: 600, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {product.title}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#1976d2", fontWeight: 700 }}>$ {product.price}</Typography>
                    <Typography sx={{ fontSize: 11, color: "#94a3b8" }}>{product.createdAt?.toDate().toLocaleDateString()}</Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </Box>}

      {/* Lightbox */}
      <Dialog open={!!lightboxImg} onClose={() => setLightboxImg(null)} maxWidth="md" PaperProps={{ sx: { bgcolor: "transparent", boxShadow: "none" } }}>
        <Box component="img" src={lightboxImg} alt="" sx={{ maxWidth: "90vw", maxHeight: "85vh", borderRadius: 2, objectFit: "contain" }} onClick={() => setLightboxImg(null)} />
      </Dialog>

      {/* Modal de edición */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullWidth maxWidth="xs" PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Editar perfil</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          <TextField label="Nombre" value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth size="small" />
          <TextField label="Teléfono" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} fullWidth size="small" />
          <TextField label="Localidad" value={editPoint} onChange={(e) => setEditPoint(e.target.value)} fullWidth size="small" />
          <TextField
            label="Estado / bio"
            value={editStatus}
            onChange={(e) => setEditStatus(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Ej: ¡Hola! Me gusta el diseño y la tecnología 👋"
            inputProps={{ maxLength: 120 }}
            helperText={`${editStatus.length}/120`}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)} sx={{ color: "#64748b" }}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ borderRadius: 2, fontWeight: 700 }}>
            {saving ? <CircularProgress size={18} /> : "Guardar"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={3000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.severity} sx={{ borderRadius: 2 }}>{snack.msg}</Alert>
      </Snackbar>

      <BottomNav />
    </Box>
  );
};

export default Miperfil;
